import { getEntireForm } from '@gef-ui/redux-common/selectors/entireForm';
import { getFormData } from '@gef-ui/redux-common/selectors/formData';
import { getRegisteredFields } from '@gef-ui/redux-common/selectors/registeredFields';
import getFilesFromBlobUrls from "@gef-ui/utils/getFilesFromBlobUrls";
import getFlattenFilesUtil from "@gef-ui/utils/getFlattenFiles";
import memoizeWithAllArguments from "@gef-ui/utils/memoizeWithAllArguments";
import omitPathsFromObject from "@gef-ui/utils/omitPathsFromObject";
import { VALID, severity } from '@gef-ui/validations/constants';
import path from "ramda/es/path";
import T from "ramda/es/T";
import always from "ramda/es/always";
import any from "ramda/es/any";
import compose from "ramda/es/compose";
import cond from "ramda/es/cond";
import equals from "ramda/es/equals";
import forEach from "ramda/es/forEach";
import map from "ramda/es/map";
import mergeAll from "ramda/es/mergeAll";
import mergeDeepRight from "ramda/es/mergeDeepRight";
import o from "ramda/es/o";
import pathOr from "ramda/es/pathOr";
import pick from "ramda/es/pick";
import propOr from "ramda/es/propOr";
import values from "ramda/es/values";
import when from "ramda/es/when";
import memoizeWithIdentity from "ramda-extension/es/memoizeWithIdentity";
import splitByDot from "ramda-extension/es/splitByDot";
import { createSelector } from 'reselect';
import { formState } from '../constants';
const validPath = pathOr(VALID, [
    'state',
    'valid'
]);
const progressPath = pathOr(false, [
    'state',
    'asyncValidationInProgress'
]);
const getValidStates = o(values, map(validPath));
const getProgressStates = o(values, map(progressPath));
const anyEquals = o(any, equals);
const validStateCond = cond([
    [
        o(anyEquals(true), getProgressStates),
        always(formState.IN_PROGRESS)
    ],
    [
        o(anyEquals(severity.ERROR), getValidStates),
        always(formState.INVALID)
    ],
    [
        T,
        always(formState.VALID)
    ]
]);
const getRegisteredFieldsFromProps = (state, { formName, fieldNames })=>when(always(fieldNames), pick(fieldNames), getRegisteredFields(formName)(state));
export const getFormValidState = createSelector(getRegisteredFieldsFromProps, validStateCond);
/**
 * get all file inputs with their deep paths
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getFileInputsWithPath(formName)(state) // {file1: 'field.file1',file2: 'file2'}
 *
 */ export const getFileInputsWithPath = memoizeWithIdentity((formName)=>createSelector(getEntireForm(formName), (form)=>compose(mergeAll, map((fileInput)=>({
                [fileInput]: path([
                    'fieldDescriptor',
                    fileInput,
                    'deepPath'
                ], form)
            })), propOr([], 'fileInputs'))(form)));
/**
 * get all files from form flatten with fieldId, errors are rejected
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getFlattenFiles(formName)(state)
 *	// result
 * 	[
 *		{ fieldId: 'file1', name: 'aa.txt' },
 *		{ fieldId: 'file2', name: 'cc.txt' },
 *		{ fieldId: 'file2', name: 'bb.txt' },
 *	]
 *
 */ export const getFlattenFiles = memoizeWithIdentity((formName)=>createSelector([
        getFileInputsWithPath(formName),
        getFormData(formName)
    ], (fileInputsWithPath, formData)=>getFlattenFilesUtil(map(splitByDot, fileInputsWithPath), formData)));
/**
 * get fromData without files
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getFormDataWithoutFiles(formName)(state)
 *
 */ export const getFormDataWithoutFiles = memoizeWithIdentity((formName)=>createSelector([
        getFileInputsWithPath(formName),
        getFormData(formName)
    ], (fileInputPaths, formData)=>omitPathsFromObject(map(splitByDot, fileInputPaths), formData)));
/**
 * Get multipart formData constructed from formData and file inputs.
 * FormData are constructed from getFormDataWithoutFiles and getFlattenFiles.
 * FlattenFiles are additionally resolved with getFilesFromBlobUrls from utils.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getMultipartFormData(formName)(state)
 *	// presudo result
 * 	FormData
 *		- prop: formData - entire form without files
 *		- prop: [fileUploadId] - file or multiple records for files
 *
 */ export const getMultipartFormData = memoizeWithIdentity((formName)=>createSelector([
        getFormDataWithoutFiles(formName),
        getFlattenFiles(formName)
    ], async (formData, flattenFiles)=>{
        const body = new FormData();
        body.append('formData', JSON.stringify(formData));
        const resolvedFiles = await getFilesFromBlobUrls(flattenFiles);
        forEach(({ fieldId, file, fileName })=>{
            body.append(fieldId, file, fileName);
        }, resolvedFiles);
        return body;
    }));
/**
 * Transforms FormData result from `getMultipartFormData` selector.
 * formData part is then transformed into blob with application/json content-type.
 * It's also possible to extend formData object with additionalData object passed as second param
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getMultipartJsonBlobFormData(formName, additionalData = {})(state)
 *	// presudo result
 * 	FormData
 *		- prop: formData - entire form without files merged with additionalData param object as application/json blob
 *		- prop: [fileUploadId] - file or multiple records for files
 *
 */ export const getMultipartJsonBlobFormData = memoizeWithAllArguments((formName, additionalData = {})=>createSelector(getMultipartFormData(formName), (formDataPromise)=>formDataPromise.then((formData)=>{
            formData.set('formData', new Blob([
                JSON.stringify(mergeDeepRight(JSON.parse(formData.get('formData')), additionalData))
            ], {
                type: 'application/json'
            }));
            return formData;
        })));
